/**
 * Bank-feed display helpers.
 *
 * For non-accountants, the user-facing "category" is always the
 * non-bank side of the double-entry. The AI returns both sides
 * (DR + CR); we pick the meaningful one per direction.
 *
 *   Inflow  → the CREDIT side (where the money came FROM)
 *   Outflow → the DEBIT  side (where the money WENT TO)
 *
 * Bank-to-bank own transfers are special-cased so we don't show
 * "Bank Account - Operating" again.
 */

// Match REAL bank / cash GL accounts (asset). Earlier this was the loose
// `/\bbank\b/i` regex, which incorrectly classified expense accounts that
// merely contain the word "bank" (e.g. "Bank Charges & Fees", "Bank
// Interest", "Long-term Bank Loans") as the bank side of the entry. That
// caused every NIP Charge / FGN Stamp Duty / Account Maintenance txn to
// show "Uncategorised" in the Category column even though the AI had
// correctly tagged them with the bank-charges expense GL.
const isBankSide = (name) => {
  if (!name) return false;
  const n = String(name).toLowerCase().trim();
  return (
    /^bank account\b/.test(n) ||
    /^bank\s*[-–-]/.test(n) ||
    /^cash\b/.test(n) ||
    /^petty cash\b/.test(n) ||
    n === 'cash and cash equivalents' ||
    /^bank balance/.test(n) ||
    /^operating cash/.test(n)
  );
};

const isBankTransfer = (tx) =>
  (tx?.ai_sub_category || '').toLowerCase().includes('transfer_between_own_accounts') ||
  // June 2026 - second legs of confirmed own-account pairs carry `transfer_pair`
  // but often have both sides on bank GLs, which used to render as "-".
  !!tx?.transfer_pair ||
  (tx?.is_transfer === true && isBankSide(tx?.ai_gl_debit_name) && isBankSide(tx?.ai_gl_credit_name));

export function pickCategoryDisplay(tx) {
  if (!tx) return { code: '', name: '-', side: 'unknown' };

  // iter1404 Phase 4b · Composite settlement (link-and-settle / manual
  // RecordSettlementModal) doesn't populate the `ai_gl_*` fields the
  // legacy classification path uses. Instead the dispatcher stamps
  // `settlement_gl_summary` with the non-bank leg(s). Prefer it here so
  // In Books rows finally show a meaningful Category instead of
  // "Uncategorised".
  const summary = tx.settlement_gl_summary;
  if (summary && summary.count > 0) {
    if (summary.multiple || summary.count > 1) {
      return {
        code: '', name: 'Multiple categories', side: 'multi',
        codes: summary.all_codes || [],
      };
    }
    return {
      code: summary.primary_code || (summary.all_codes || [])[0] || '',
      name: summary.primary_name || (summary.all_names || [])[0] || 'Uncategorised',
      side: 'settlement',
    };
  }

  if (isBankTransfer(tx)) {
    return { code: tx.ai_gl_credit || tx.gl_credit, name: 'Bank Transfer', side: 'transfer' };
  }

  // Bank charges (NIP fees, account maintenance, stamp duty, etc.) are
  // outflows where ONE leg is missing in the stored AI payload. They
  // always belong under the Bank Charges & Fees expense account - surface
  // that explicitly so the Category column stops showing "Uncategorised"
  // for what is the most common transaction type by volume.
  if (tx.is_bank_charge) {
    const code =
      (!isBankSide(tx.ai_gl_debit_name) && tx.ai_gl_debit) ||
      (!isBankSide(tx.ai_gl_credit_name) && tx.ai_gl_credit) ||
      (!isBankSide(tx.gl_name) && tx.gl_code) ||
      '6700';
    const name =
      (!isBankSide(tx.ai_gl_debit_name) && tx.ai_gl_debit_name) ||
      (!isBankSide(tx.ai_gl_credit_name) && tx.ai_gl_credit_name) ||
      (!isBankSide(tx.gl_name) && tx.gl_name) ||
      'Bank Charges & Fees';
    return { code, name, side: 'expense' };
  }

  // iter1101-fix - prefer tx.direction; falling back to amount>0 only
  // when direction is missing. See BankFeedCenterPage.jsx:4193 note.
  const isInflow = tx.direction
    ? tx.direction === 'inflow'
    : (tx.amount || 0) > 0;
  // Inflow → non-bank side is the credit; Outflow → non-bank side is the debit.
  const primary = isInflow
    ? { code: tx.ai_gl_credit || tx.gl_credit, name: tx.ai_gl_credit_name || tx.gl_credit_name }
    : { code: tx.ai_gl_debit  || tx.gl_debit,  name: tx.ai_gl_debit_name  || tx.gl_debit_name  };
  const fallback = isInflow
    ? { code: tx.ai_gl_debit  || tx.gl_debit,  name: tx.ai_gl_debit_name  || tx.gl_debit_name  }
    : { code: tx.ai_gl_credit || tx.gl_credit, name: tx.ai_gl_credit_name || tx.gl_credit_name };

  // If the primary "category" side is actually a bank account (which can
  // happen for own-account flows the AI didn't tag as a transfer), fall back
  // to the other side or "Uncategorised".
  if (!primary.name || isBankSide(primary.name)) {
    if (fallback.name && !isBankSide(fallback.name)) {
      return { code: fallback.code, name: fallback.name, side: isInflow ? 'debit' : 'credit' };
    }
    return { code: '', name: 'Uncategorised', side: 'unknown' };
  }

  return { code: primary.code, name: primary.name, side: isInflow ? 'credit' : 'debit' };
}

export function getTaxAmounts(tx) {
  // iter1101-fix - see pickCategoryDisplay for the direction/amount rule.
  const isInflow = tx?.direction
    ? tx.direction === 'inflow'
    : (tx?.amount || 0) > 0;
  const vatAmount = isInflow
    ? Number(tx?.vat_amount || tx?.output_vat_amount || 0)
    : Number(tx?.input_vat_amount || tx?.vat_amount || 0);
  const vatRate = isInflow
    ? Number(tx?.vat_rate || tx?.output_vat_rate || 0)
    : Number(tx?.input_vat_rate || tx?.vat_rate || 0);
  const whtAmount = Number(tx?.wht_amount || 0);
  const whtRate = Number(tx?.wht_rate || 0);
  return {
    vatAmount: Number.isFinite(vatAmount) ? vatAmount : 0,
    vatRate: vatRate < 1 ? vatRate * 100 : vatRate, // normalise frac to %
    whtAmount: Number.isFinite(whtAmount) ? whtAmount : 0,
    whtRate: whtRate < 1 ? whtRate * 100 : whtRate,
    isInflow,
  };
}
